import type { UserType } from "@/types/user.type";
import {
  Building,
  Calendar,
  CircleCheckBig,
  CircleSlash2,
  Clock,
  Key,
  Mail,
  MapPin,
  Phone,
  Shield,
  User,
} from "lucide-react";
import { Badge } from "./ui/badge";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface UserInfosProps {
  user: UserType;
}

export const UserInfos = ({ user }: UserInfosProps) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Prénom */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <User className="h-4 w-4" />
          Prénom
        </label>
        <div className="p-3 bg-muted/50 border-b-2 rounded-md">
          <p className="text-sm font-medium">{user?.firstName || "-"}</p>
        </div>
      </div>

      {/* Nom de famille */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <User className="h-4 w-4" />
          Nom de famille
        </label>
        <div className="p-3 bg-muted/50 border-b-2 rounded-md">
          <p className="text-sm font-medium">{user?.lastName || "-"}</p>
        </div>
      </div>

      {/* Email */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Mail className="h-4 w-4" />
          Email
        </label>
        <div className="p-3 bg-muted/50 border-b-2 rounded-md">
          <p className="text-sm font-medium">{user?.email || "-"}</p>
        </div>
      </div>

      {/* Téléphone */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Phone className="h-4 w-4" />
          Téléphone
        </label>
        <div className="p-3 bg-muted/50 border-b-2 rounded-md">
          <p className="text-sm font-medium">{user?.phoneNumber || "-"}</p>
        </div>
      </div>

      {/* Adresse */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          Adresse
        </label>
        <div className="p-3 bg-muted/50 border-b-2 rounded-md">
          <p className="text-sm font-medium">{user?.address || "-"}</p>
        </div>
      </div>

      {/* Code postal */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          Code postal
        </label>
        <div className="p-3 bg-muted/50 border-b-2 rounded-md">
          <p className="text-sm font-medium">{user?.postalCode || "-"}</p>
        </div>
      </div>

      {/* Ville */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          Ville
        </label>
        <div className="p-3 bg-muted/50 border-b-2 rounded-md">
          <p className="text-sm font-medium">{user?.city || "-"}</p>
        </div>
      </div>

      {/* Matricule */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Key className="h-4 w-4" />
          Matricule
        </label>
        <div className="p-3 bg-muted/50 border-b-2 rounded-md">
          <p className="text-sm font-medium">{user?.employeeNumber || "-"}</p>
        </div>
      </div>

      {/* PIN ou Email selon le rôle */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Key className="h-4 w-4" />
          {user?.role === "ADMIN" ? "Identifiant" : "PIN"}
        </label>
        <div className="p-3 bg-muted/50 border-b-2 rounded-md">
          <p className="text-sm font-mono">
            {user?.role === "ADMIN" ? user?.email : `PIN: ${user?.pin || "-"}`}
          </p>
        </div>
      </div>

      {/* Rôle */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Shield className="h-4 w-4" />
          Rôle
        </label>
        <div className="p-3 bg-muted/50 border-b-2 rounded-md">
          <Badge variant="outline" className="font-medium">
            {user?.role === "ADMIN"
              ? "Administrateur"
              : user?.role === "EMPLOYEE"
              ? "Employé"
              : "Super Utilisateur"}
          </Badge>
        </div>
      </div>

      {/* Entreprise */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Building className="h-4 w-4" />
          Entreprise
        </label>
        <div className="p-3 bg-muted/50 border-b-2 rounded-md">
          <p className="text-sm font-medium">{user?.company?.name || "-"}</p>
        </div>
      </div>

      {/* Statut */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <CircleCheckBig className="h-4 w-4" />
          Statut
        </label>
        <div className="p-3 bg-muted/50 border-b-2 rounded-md">
          <Badge
            variant={user?.isActive ? "default" : "secondary"}
            className={
              user?.isActive
                ? "bg-success hover:bg-success"
                : "bg-error hover:bg-error text-error-foreground"
            }
          >
            {user?.isActive ? (
              <>
                <CircleCheckBig className="h-3 w-3 mr-1" />
                Actif
              </>
            ) : (
              <>
                <CircleSlash2 className="h-3 w-3 mr-1" />
                Inactif
              </>
            )}
          </Badge>
        </div>
      </div>

      {/* Date de création */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Date de création
        </label>
        <div className="p-3 bg-muted/50 border-b-2 rounded-md">
          <p className="text-sm font-medium">
            {user?.createdAt
              ? format(new Date(user.createdAt), "PPP", { locale: fr })
              : "-"}
          </p>
        </div>
      </div>

      {/* Date de modification */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Dernière modification
        </label>
        <div className="p-3 bg-muted/50 border-b-2 rounded-md">
          <p className="text-sm font-medium">
            {user?.updatedAt
              ? format(new Date(user.updatedAt), "PPP", { locale: fr })
              : "-"}
          </p>
        </div>
      </div>
    </div>
  );
};
